/**
 * OrgChangeCreateForm — Step 1.5 of OrgChangeStepper (v0.14 Wave 3)
 *
 * Inline "create a brand new client" form. Two fields: name + legal_name.
 * Per spec §6.1-6.4:
 *   - Name pre-fills from picker search value (or DBA name in standalone path)
 *   - legal_name defaults to name; user can override
 *   - On submit: POST /api/organizations
 *   - On 409 duplicate: parent handles auto-pick existing (§3.4 / §6.5)
 *   - On cancel-from-form (Back): picker state preserved (§6.3)
 *   - On cancel-after-create (Step 3 cancel): backend persists (§6.4) —
 *     parent stepper handles the toast.
 *
 * Form-level rules per §6.6 normalization happen at the backend boundary;
 * here we just trim + min-length validate.
 */
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export interface OrgChangeCreateFormProps {
  name: string;
  legalName: string;
  onNameChange: (v: string) => void;
  onLegalNameChange: (v: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function OrgChangeCreateForm({
  name,
  legalName,
  onNameChange,
  onLegalNameChange,
  onSubmit,
  onBack,
  isSubmitting,
}: OrgChangeCreateFormProps) {
  const nameValid = name.trim().length > 0;
  const tooLong = name.trim().length > 200 || legalName.trim().length > 255;
  const canSubmit = nameValid && !tooLong && !isSubmitting;

  return (
    <div className="space-y-4" data-testid="org-change-create-form">
      <div>
        <h3 className="text-base font-semibold text-slate-900">
          Create a new client
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Just the name + legal entity name for now — you can add address,
          contacts, and DBAs later from the client&apos;s page.
        </p>
        <p className="text-xs text-slate-500 mt-1 italic">
          Heads up: don&apos;t navigate away — your draft will be lost.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="org-change-create-name"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"
          >
            Client name <span className="text-red-600">*</span>
          </label>
          <Input
            id="org-change-create-name"
            autoFocus
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Nuovo Renovations Inc"
            maxLength={210}
            data-testid="org-change-create-name"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
          {!nameValid && (
            <p className="text-xs text-amber-700 mt-1">
              Client name is required.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="org-change-create-legal-name"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"
          >
            Legal entity name
          </label>
          <Input
            id="org-change-create-legal-name"
            value={legalName}
            onChange={(e) => onLegalNameChange(e.target.value)}
            placeholder="Same as client name unless different"
            maxLength={260}
            data-testid="org-change-create-legal-name"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
          <p className="text-xs text-slate-500 mt-1">
            The legal name prints on invoices. Defaults to the client name.
          </p>
        </div>

        {tooLong && (
          <p className="text-xs text-amber-700">
            Names must be 200 / 255 characters or fewer.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="org-change-create-back"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to picker
        </button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          data-testid="org-change-create-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Creating...
            </>
          ) : (
            'Create client'
          )}
        </Button>
      </div>
    </div>
  );
}
